// Perfil del cliente: datos de empresa + usuario. Link para editar.

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useClientLead, useUpdateLead } from '@/hooks/use-store';
import { StoreBreadcrumbs } from '@/components/store/StoreBreadcrumbs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { User, Building2, Mail, Phone, MapPin, LogOut } from 'lucide-react';

const StoreProfile = () => {
  const navigate = useNavigate();
  const { data: lead, isLoading } = useClientLead();
  const updateLead = useUpdateLead();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', phone: '', country: '', city: '', phoneHasWhatsapp: false });

  useEffect(() => {
    if (lead) {
      setForm({
        name: lead.name ?? '',
        company: lead.company ?? '',
        phone: lead.phone ?? '',
        country: lead.country ?? '',
        city: lead.city ?? '',
        phoneHasWhatsapp: !!(lead.whatsapp?.trim()),
      });
    }
  }, [lead]);

  const handleSave = async () => {
    if (!lead?.id) return;
    try {
      await updateLead.mutateAsync({
        id: lead.id,
        name: form.name || undefined,
        company: form.company || undefined,
        phone: form.phone || undefined,
        country: form.country || undefined,
        city: form.city || undefined,
        whatsapp: form.phoneHasWhatsapp && form.phone?.trim() ? form.phone.trim() : undefined,
      });
      toast({ title: 'Datos actualizados' });
      setIsEditing(false);
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'No se pudo actualizar', variant: 'destructive' });
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.dispatchEvent(new Event('quotation-cart-cleared'));
    navigate('/store', { replace: true });
  };

  const breadcrumbs = [
    { label: 'Store', path: '/store' },
    { label: 'Mi perfil' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <StoreBreadcrumbs segments={breadcrumbs} />
        <p className="text-zinc-600">Cargando...</p>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="space-y-4">
        <StoreBreadcrumbs segments={breadcrumbs} />
        <p className="text-zinc-600">Debe iniciar sesión para ver su perfil.</p>
        <Button asChild variant="outline">
          <Link to="/store/login">Iniciar sesión</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StoreBreadcrumbs segments={breadcrumbs} />
      <div>
        <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Mi perfil</h1>
        <p className="mt-1 text-sm text-zinc-600">Datos de empresa y contacto</p>
      </div>

      {!isEditing ? (
        <>
          <Card className="border-zinc-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4" />
                Usuario
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-zinc-400" />
                <span>{lead.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-zinc-400" />
                <span>{lead.name || '—'}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-zinc-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-4 w-4" />
                Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-zinc-400" />
                <span>{lead.company || '—'}</span>
              </div>
              {lead.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-zinc-400" />
                  <span>{lead.phone}</span>
                  {lead.whatsapp && <span className="text-xs text-zinc-500">(WhatsApp disponible)</span>}
                </div>
              )}
              {(lead.country || lead.city) && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-zinc-400" />
                  <span>{[lead.city, lead.country].filter(Boolean).join(', ') || '—'}</span>
                </div>
              )}
            </CardContent>
          </Card>
          <Button variant="outline" onClick={() => setIsEditing(true)}>
            Editar datos de empresa y usuario
          </Button>
        </>
      ) : (
        <Card className="border-zinc-200">
          <CardHeader>
            <CardTitle>Editar datos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Nombre</label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="h-9" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Empresa</label>
              <Input value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} className="h-9" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Teléfono (con código de país)</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="h-9"
                placeholder="Ej: +34 612 345 678"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="profile_phone_has_whatsapp"
                checked={form.phoneHasWhatsapp}
                onCheckedChange={(v) => setForm((f) => ({ ...f, phoneHasWhatsapp: v === true }))}
              />
              <label htmlFor="profile_phone_has_whatsapp" className="text-sm cursor-pointer">
                Este teléfono tiene WhatsApp para contactar rápido
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-500 block mb-1">País</label>
                <Input value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} className="h-9" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 block mb-1">Ciudad</label>
                <Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className="h-9" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={updateLead.isPending}>
                {updateLead.isPending ? 'Guardando...' : 'Guardar'}
              </Button>
              <Button variant="ghost" onClick={() => setIsEditing(false)}>
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2">
          <LogOut className="h-4 w-4" />
          Cerrar sesión
        </Button>
      </div>
    </div>
  );
};

export default StoreProfile;
